import { DoctoralContactHub } from '@/features/doctoral/components/DoctoralContactHub';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { routes } from '@/lib/routes';
import { advisorApi } from '@/features/workspace/api';
import { ModuleGrid, WorkspaceHero } from '@/features/workspace/components/WorkspaceShell';
import { ROLE_MODULE_THEMES } from '@/features/workspace/roleModuleThemes';
import { WorkspaceModulePage } from '@/features/workspace/WorkspaceModulePage';
import type { WorkspaceSummary } from '@/features/workspace/types';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { AdvisorExchangePage } from './components/AdvisorExchangePage';
import { AdvisorReviewQueuePage } from './components/AdvisorReviewQueuePage';
import { AdvisorDoctoralProcessPage } from '@/features/advisor/components/AdvisorDoctoralProcessPage';
import { AdvisorMonitoringPage } from '@/features/advisor/components/AdvisorMonitoringPage';
import { AdvisorPointsPage } from '@/features/advisor/components/AdvisorPointsPage';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

const AdvisorHome = ({ summary }: { summary: WorkspaceSummary }) => (
  <CabinetWorkspaceFrame
    links={{
      notebook: `${routes.advisor}/files`,
      calendar: `${routes.advisor}/meetings`,
      meetings: `${routes.advisor}/meetings`,
      connections: `${routes.advisor}/connections`,
      alliances: `${routes.advisor}/connections`,
      qa: routes.portalSection('instructions'),
      support: routes.portalSection('contacts'),
    }}
  >
    <WorkspaceHero
      profile={summary.profile}
      kpis={summary.kpis}
      createRequestTo={routes.newRequest}
      assistantTo={routes.assistant}
      accent="#49ddb7"
    />
    <section className="role-cabinet">
      <ModuleGrid
        modules={summary.modules}
        basePath={routes.advisor}
        theme={ROLE_MODULE_THEMES.advisor}
      />
    </section>
    <ScientificGpsBlock openTo={`${routes.advisor}/monitoring`} />
  </CabinetWorkspaceFrame>
);

const AdvisorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [error, setError] = useState(false);
  const backToCabinet = () => navigate(routes.advisor);
  const canAccess = hasRole('ADVISOR') || hasRole('SYSTEM_OWNER');

  useEffect(() => {
    let cancelled = false;
    if (!canAccess) return;
    setError(false);
    advisorApi
      .workspace()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, canAccess]);

  if (!canAccess) {
    return <Navigate to={routes.denied} replace />;
  }

  if (error) {
    return <Navigate to={routes.denied} replace />;
  }

  if (!summary) {
    return <section className="dashboard workspace-page" />;
  }

  return (
    <Routes>
      <Route index element={<AdvisorHome summary={summary} />} />
      <Route
        path="doctoral_process"
        element={<AdvisorDoctoralProcessPage onClose={backToCabinet} />}
      />
      <Route
        path="doctoral_process/*"
        element={<AdvisorDoctoralProcessPage onClose={backToCabinet} />}
      />
      <Route path="monitoring" element={<AdvisorMonitoringPage onClose={backToCabinet} />} />
      <Route path="monitoring/*" element={<AdvisorMonitoringPage onClose={backToCabinet} />} />
      <Route path="messages" element={<DoctoralContactHub advisor onClose={backToCabinet} />} />
      <Route
        path="plans"
        element={<AdvisorReviewQueuePage kind="plans" onClose={backToCabinet} />}
      />
      <Route
        path="attestation"
        element={<AdvisorReviewQueuePage kind="attestation" onClose={backToCabinet} />}
      />
      <Route path="points" element={<AdvisorPointsPage onClose={backToCabinet} />} />
      <Route path="points/*" element={<AdvisorPointsPage onClose={backToCabinet} />} />
      <Route
        path="connections"
        element={
          <RoleAcademicConnectionsOverlay
            isOpen
            onClose={backToCabinet}
            roleContext="ADVISOR"
            roleTitle="Эдвайзер"
          />
        }
      />
      <Route
        path="tools/*"
        element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path="meetings"
        element={<AdvisorExchangePage advisor view="meetings" onClose={backToCabinet} />}
      />
      <Route
        path="meetings/*"
        element={<AdvisorExchangePage advisor view="meetings" onClose={backToCabinet} />}
      />
      <Route
        path="files/*"
        element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
      />
      <Route
        path=":moduleKey"
        element={
          <WorkspaceModulePage
            modules={summary.modules}
            basePath={routes.advisor}
            loadModule={(key, params) =>
              advisorApi.module(key, {
                tab: params.tab,
                filter: params.filter,
                search: params.search || undefined,
                candidate_id: params.candidateId,
              })
            }
          />
        }
      />
    </Routes>
  );
};

export default AdvisorPage;
